import Rails from "@rails/ujs"

/*
 * Implements a user-facing modal confirmation when link has a
 * "data-confirm" attribute using Tailwind. MIT license.
 * 
 * AlpineJS is used to manage animations and displaying the modal itself.
 *
 *   - ruben@21ninjas.es  Tue Mar  11 12:28:15 CEST 2021
 */


(() => {

  let defaults = {
    title: 'Are you sure?',
    commit: 'Confirm',
    commitClass: 'btn-danger',
    cancel: 'Cancel',
    cancelClass: 'btn-default',
    focus: 'commit'
  };


  /*
   * createModal
   * Creates the modal
   *
   */

  let createModal = (ele) => {

    let element = ele.target;

    let options = {
      title: element.dataset['title'],
      text: element.dataset['confirm'],
      commit: element.dataset['commit'],
      commitClass: element.dataset['commitclass'],
      cancel: element.dataset['cancel'],
      cancelClass: element.dataset['cancelclass'],
      method: element.dataset['method'],
      url: element.href
    };

    let modal = createModalElement(options);

    modal
      .querySelector('.commit')
      .addEventListener('click', () => {
        closeAndDestroyModal(modal);
      });

    modal
      .querySelector('.cancel')
      .addEventListener('click', () => {
        closeAndDestroyModal(modal);
      });

    modal
      .querySelector('.close')
      .addEventListener('click', () => {
        closeAndDestroyModal(modal);
      });

    return modal;
  };


  /* 
   * closeAndDestroyModal
   * Closes modal firstly in order AlpineJS
   * animations to perfom and destroys modal
   * element afterwards
   *
   */

  let closeAndDestroyModal = (modalObj) => {
    
    // Applying animations
    let overlay = modalObj.querySelector('.overlay');
    overlay.classList.remove('ease-out', 'duration-300', 'translate-y-0', 'opacity-100', 'sm:translate-x-0', 'opacity-100');
    overlay.classList.add('transition', 'ease-in', 'duration-100', 'opacity-0');

    let modal = modalObj.querySelector('.modal');
    modal.classList.remove('ease-out', 'duration-300', 'translate-y-0', 'opacity-100', 'sm:translate-x-0');
    modal.classList.add('transition', 'ease-in', 'duration-100', 'opacity-0');

    setTimeout(() => {
      modalObj.remove();
    }, 200);
  };


  /*
   * createModalElement
   * Creates the modal element and set it up
   * according to the default or user's preferences
   *
   */

  let createModalElement = (options) => {

    let modal = document.createElement('div');

    modal.classList.add('fixed', 'z-10', 'inset-0', 'overflow-y-auto');   
    modal.innerHTML = `
      <div id="modal" class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">

        <div class="overlay fixed inset-0 transform ease-out duration-300 transition translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2 z-0" aria-hidden="true">
          <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div class="modal inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6 transform ease-out duration-300 transition translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2" role="dialog" aria-modal="true" aria-labelledby="modal-headline">
          <div class="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
            <button type="button" class="close bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
              <span class="sr-only">Close</span>
              <!-- Heroicon name: outline/x -->
              <svg class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div class="sm:flex sm:items-start">
            <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
              <!-- Heroicon name: outline/exclamation -->
              <svg class="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <h3 class="modal-title text-lg leading-6 font-medium text-gray-900" id="modal-headline">
                Are you sure?
              </h3>
              <div class="mt-2">
                <p class="modal-text text-sm text-gray-500">
                  Are you sure you want to perform this action?
                </p>
              </div>
            </div>
          </div>
          <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
            <a class="commit btn btn-danger w-full inline-flex justify-center sm:ml-3 sm:w-auto">
              Confirm
            </a>
            <button type="button" class="cancel btn mt-3 w-full inline-flex justify-center sm:mt-0 sm:w-auto" @click="modal = false">
              Cancel
            </button>
          </div>
        </div>
      </div>
    `;

    modal.querySelector('.commit').setAttribute('href', options.url);
    modal.querySelector('.commit').setAttribute('data-method', options.method);

    if ( options.title )
      modal.querySelector('.modal-title').innerHTML = options.title;

    if ( options.text )
      modal.querySelector('.modal-text').innerHTML = options.text;

    if ( options.commit )
      modal.querySelector('.commit').innerHTML = options.commit;

    if ( options.cancel )
      modal.querySelector('.cancel').innerHTML = options.cancel;

    if ( options.commitClass ) {
      modal.querySelector('.commit').classList.remove('btn-danger');
      modal.querySelector('.commit').classList.add(options.commitClass);
    }

    if ( options.cancelClass ) {
      modal.querySelector('.cancel').classList.remove('btn');
      modal.querySelector('.cancel').classList.add(options.cancelClass);
    }

    document.body.appendChild(modal);

    return modal;
  };


  if ( Rails ) {

    let elements = ['a[data-confirm]', 'button[data-confirm]', 'input[type=submit][data-confirm]'];

    Rails.delegate(document, elements.join(', '), 'confirm', (element) => {

      let modalObj = createModal(element);

      // Applying animations
      setTimeout(() => {
        let overlay = modalObj.querySelector('.overlay');
        overlay.classList.remove('translate-y-2', 'opacity-0', 'sm:translate-y-0', 'sm:translate-x-2');
        overlay.classList.add('translate-y-0', 'opacity-100', 'sm:translate-x-0');
  
        let modal = modalObj.querySelector('.modal');
        modal.classList.remove('translate-y-2', 'opacity-0', 'sm:translate-y-0', 'sm:translate-x-2');
        modal.classList.add('translate-y-0', 'opacity-100', 'sm:translate-x-0');
      }, 100);

      return false;

    });

  }
})();